import { useCallback, useEffect, useState } from 'react';
import { SaleFulfillmentDetail } from '../types/sales.types';
import {
  fetchSaleFulfillmentDetail,
  getSaleFulfillmentCacheEntry,
  subscribeSaleFulfillmentDetail,
} from '../services/saleFulfillmentDetailCache';

interface UseSaleFulfillmentDetailOptions {
  /** Fetch immediately when the hook mounts / orderId changes. Defaults to true. */
  autoFetch?: boolean;
  /**
   * When true, always hit the network on mount/orderId change (still deduped
   * with other subscribers). Use for surfaces that must show fresh logistics
   * (e.g. order details drawer on open).
   */
  refetchOnMount?: boolean;
}

interface UseSaleFulfillmentDetailResult {
  detail: SaleFulfillmentDetail | null;
  isLoading: boolean;
  error: string | null;
  /** Force re-fetch; updates every subscriber of this orderId. */
  fetchDetail: () => Promise<void>;
}

/**
 * Shared-cache reader for mixed-branch sale fulfillment
 * (`GET /sales-orders/:id/fulfillment`). Expanded list rows and the details
 * drawer subscribe to the same entry so they cannot diverge.
 */
export const useSaleFulfillmentDetail = (
  orderId: number | null,
  { autoFetch = true, refetchOnMount = false }: UseSaleFulfillmentDetailOptions = {},
): UseSaleFulfillmentDetailResult => {
  const [, setVersion] = useState(0);

  useEffect(
    () =>
      subscribeSaleFulfillmentDetail((changedId) => {
        if (changedId == null || orderId == null || changedId === orderId) {
          setVersion((v) => v + 1);
        }
      }),
    [orderId],
  );

  const entry = getSaleFulfillmentCacheEntry(orderId);

  const fetchDetail = useCallback(
    async (options: { force?: boolean } = {}) => {
      if (orderId == null) return;
      try {
        await fetchSaleFulfillmentDetail(orderId, options);
      } catch {
        // Error is stored on the shared cache entry for all subscribers.
      }
    },
    [orderId],
  );

  useEffect(() => {
    if (!autoFetch || orderId == null) return;
    void fetchDetail({ force: refetchOnMount });
  }, [orderId, autoFetch, refetchOnMount, fetchDetail]);

  const isLoading = entry?.status === 'loading' || (autoFetch && orderId != null && entry == null);

  return {
    detail: entry?.detail ?? null,
    isLoading,
    error: entry?.error ?? null,
    fetchDetail: () => fetchDetail({ force: true }),
  };
};
